import { useLayoutEffect, type ReactElement } from 'react'

import { useRailFitContext, type RailBlockId } from '@/features/market-rail/useRailFit'

interface RailRowsProps {
  /** The block this list belongs to: its count comes from the fit under this id. */
  block: RailBlockId
  /** Every candidate row, keyed, already sliced to the card's own cap. */
  rows: readonly ReactElement[]
}

/**
 * A rail list's rows: as many as the rail's fit gives this block, and the rest
 * PARKED rather than unmounted — kept at the list's own width in an invisible,
 * inert, zero-height layer, so `measureRail` knows what each one would cost to
 * bring back. Every list in every rail draws through this, rows and skeleton
 * rows alike, so the one fit trims all of them.
 *
 * The parked layer is `aria-hidden` and `inert` (nothing in it can be reached
 * or read) and clips to its zero height, so it never adds scroll to the page.
 * The sentinel after the parked rows keeps every one of them off
 * `:last-child`, so none drops its hairline the way the last SHOWN row does.
 *
 * With no fit (unmeasured, or outside a fitted rail) it draws every row.
 */
export default function RailRows({ block, rows }: RailRowsProps) {
  const { fit, remeasure } = useRailFitContext()
  const count = fit?.rows[block] ?? rows.length

  // A new set of rows (data landing over the skeleton, a refetch) is
  // re-measured before the browser paints it, rather than a frame later when
  // the rail's ResizeObserver would notice: rows that wrap taller than the
  // ones they replaced would otherwise overrun the rail for that frame.
  const signature = rows.map((row) => row.key).join('|')
  useLayoutEffect(() => {
    remeasure()
  }, [remeasure, signature])

  return (
    <div data-rail-list={block} className="relative">
      <div data-rail-rows="">{rows.slice(0, count)}</div>
      {count < rows.length ? (
        <div
          data-rail-parked=""
          aria-hidden="true"
          inert
          className="pointer-events-none invisible absolute inset-x-0 top-0 h-0 overflow-clip"
        >
          {rows.slice(count)}
          <span data-rail-sentinel="" hidden />
        </div>
      ) : null}
    </div>
  )
}
